import {
  calculateTotalPricingBySpecialty,
  extractUniqueSpecialties,
  groupCptCodesBySpecialty,
  sortSpecialtiesByPricing,
} from "helpers/price";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import React, { useMemo } from "react";
import { useGetPrecalculatedAllowablesQuery } from "store/queries/pricing";
import styled from "styled-components";

import {
  IChargeBasedPricingLineDetail,
  IClaim,
  IInpatientOutlierCalculation,
  ILesserOfChargesCalculation,
  ILineItem,
} from "../../types/claim";
import type {
  ICalculatedData,
  IProviderProcedureData,
} from "../../types/precalculatedAllowables.types";
import { effectiveLineAllowed } from "../../utils/claimAdjustments";
import {
  ChargeBasedPricingBlock,
  formatMultiplierPercent,
} from "./ChargeBasedPricingBlock";
import { ErrorMessage } from "./ErrorMessage";
import { NoDataMessage } from "./NoDataMessage";
import { prorateExpectedByCpt } from "./proration";
import {
  descriptionGroupKey,
  parseOrdinalProcedurePrefix,
  SpecialtyTabContent,
} from "./SpecialtyTabContent";

interface IPassThroughCalculation {
  type: string;
  totalPassThroughAmountInCents: number;
  codes: {
    code: string;
    inpatientRulesetId: string;
    chargeMultiplier: number;
    thresholdInCents: number;
    chargeInCents: number;
    calculatedAmountInCents: number;
  }[];
}

interface ILosOutlierCalculation {
  pricingRule: string;
  ruleApplied: boolean;
  ruleStatus: string;
  drgBaseRateInCents: number;
  alos: number | null;
  los: number | null;
  losSource: string | null;
  threshold?: number | null;
  multiplier?: number;
  outlierDays?: number;
  perDiemInCents?: number;
  outlierAddOnInCents?: number;
  expectedAllowedInCents?: number;
  dischargeStatus?: string;
  transferPaymentInCents?: number;
  drgCode?: string;
}

interface IPrecalculatedAllowablesContentProps {
  accountId: string;
  payerId: number;
  caseSignature: string;
  /**
   * CLE-2299: pass the claim's date of service so the lookup prefers the date-specific
   * precalc row over a stale canonical (NULL-serviceDate) row. Without this the tab can
   * silently show pricing that disagrees with the claim's own expectedAmount after a
   * config-driven repricing.
   */
  dateOfService?: string;
  skip?: boolean; // Optional skip flag for controlling when to fetch
  claim?: IClaim; // Optional claim data for NCCI bypass display
  // CLE-2489: when this block renders the 837 "Expected Amount (837 Case Signature)" section, prefer the
  // claim's 837 precalc (edi837PrecalculatedAllowable) over the 835 one — otherwise the 837 breakdown
  // shows the 835's CPT codes. Defaults false (835 section).
  preferEdi837?: boolean;
  // CLE-3071: when this block renders the "Expected Amount (rev code)" section, read the claim's
  // revenue-code precalc (revCodePrecalculatedAllowable). Never fetches: the section is only rendered
  // when the claim carries that precalc, and the generic lookup would return the CPT pricing.
  preferRevCode?: boolean;
  lineItems?: ILineItem[]; // Optional line items from the claim, displayed in Claim Allowables table
  outlierPaymentInCents?: number | null; // High-cost outlier payment to include in totals
  outlierCalculation?: {
    inpatientRulesetId?: string;
    type?: string;
    costOutlierMethod?: string;
    costToChargeRatio: number;
    fixedLossThresholdInCents: number;
    outlierPercent: number;
    estimatedCostInCents: number;
    triggerAmountInCents: number;
  } | null;
  /** Pass-through calculation data for codes without fee schedule match */
  passThroughCalculation?: IPassThroughCalculation | null;
  /** Total charge amount from the claim (cents), used for outlier display */
  chargeAmountInCents?: number | null;
  /** LOS outlier / transfer calculation data from DRG pricing */
  losOutlierCalculation?: ILosOutlierCalculation | null;
  /** Stop loss payment amount (CLE-1897) */
  stopLossPaymentInCents?: number | null;
  /** Stop loss calculation details (CLE-1897) */
  stopLossCalculation?: {
    inpatientRulesetId: string;
    type: string;
    thresholdMultiplier: number;
    paymentPercent: number;
    thresholdAmountInCents: number;
    chargeAmountInCents: number;
  } | null;
  /** Charge outlier payment amount (CLE-2120) */
  chargeOutlierPaymentInCents?: number | null;
  /** Charge outlier calculation details (CLE-2120) */
  chargeOutlierCalculation?: {
    inpatientRulesetId: string;
    type: string;
    thresholdType: string;
    thresholdAmountInCents: number;
    outlierPercent: number;
    chargeFloorInCents?: number | null;
    chargeAmountInCents: number;
    edi837RawBaseExpected: number;
    excessChargesInCents?: number;
    chargeOutlierPaymentInCents?: number;
  } | null;
  /** Inpatient outlier — composable trigger/formula/mode rule engine (CLE-2120 v3) */
  inpatientOutlierCalculation?: IInpatientOutlierCalculation | null;
  /** Lesser-of-charges cap — applied last, caps expected at billed charges × multiplier */
  lesserOfChargesCalculation?: ILesserOfChargesCalculation | null;
}

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const TotalAllowableSection = styled.div`
  padding: 20px 20px 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const TotalAllowableLabel = styled.div`
  font-size: 16px;
  color: #777;
  font-weight: 400;
`;

const TotalAllowableAmount = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: #000;
`;

const MissingCodesSection = styled.div`
  padding: 16px 20px;
  background-color: #fff3cd;
  border-left: 4px solid #ffc107;
  margin-bottom: 16px;
`;

const MissingCodesTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #856404;
  margin-bottom: 8px;
`;

const MissingCodesText = styled.div`
  font-size: 13px;
  color: #856404;
  line-height: 1.5;
`;

const ClaimAllowablesSection = styled.div`
  padding: 12px 20px 20px 20px;
  border-top: 1px solid #e0e0e0;
`;

const ClaimAllowablesTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

/**
 * Format specialty name from CONSTANT_CASE to Title Case
 * E.g., "PRIMARY_PROVIDER" -> "Primary Provider"
 */
function formatSpecialtyName(specialty: string): string {
  return specialty
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Reusable content component for displaying precalculated allowables data
 * Can be used standalone or within a modal
 */
export function PrecalculatedAllowablesContent({
  accountId,
  payerId,
  caseSignature,
  dateOfService,
  skip = false,
  claim,
  preferEdi837 = false,
  preferRevCode = false,
  lineItems,
  outlierPaymentInCents,
  outlierCalculation,
  passThroughCalculation,
  chargeAmountInCents,
  losOutlierCalculation,
  stopLossPaymentInCents,
  stopLossCalculation,
  chargeOutlierPaymentInCents,
  chargeOutlierCalculation,
  inpatientOutlierCalculation,
  lesserOfChargesCalculation,
}: IPrecalculatedAllowablesContentProps) {
  // Charge-Based Pricing prices this claim off billed charges rather than a fee schedule, so
  // the rule audit is rendered from the claim object rather than the precalc.
  const chargeBasedCalculation = claim?.chargeBasedPricingCalculation ?? null;
  const isChargeBasedPricing = !!chargeBasedCalculation;

  // CLE-2600 — how much of this claim the charge-based rule actually owns decides the layout.
  //
  // When the rule prices off the claim TOTAL there are no per-line numbers at all, so the block is
  // the only thing that can be rendered and the precalc fetch is skipped — the lookup 404s for
  // those claims anyway. Same for a claim whose `perLines` predates CLE-2546.
  //
  // A FULLY-OWNED LINE_CHARGES claim (the common shape — thousands in prod) sits in between: the
  // fee schedule contributed nothing to the expected, so its contracted-rate / calculation detail
  // would describe pricing that never happened, but the per-line comparison IS correct and is what
  // claim review actually needs. Those claims get the block plus the Claim Allowables table, and
  // nothing else. The table renders even when the precalc lookup found nothing, because for these
  // claims `perLines` — not the precalc — is what fills the Expected and Method columns.
  //
  // A PARTIAL LINE_CHARGES claim is the opposite case: since CLE-2546 (per-line ownership /
  // ONLY_IF_NO_FEE_SCHEDULE) and CLE-2600 (bundling wins over the gap-filler) most lines on these
  // claims ARE fee-schedule priced, the lesser-of cap DOES apply, and bundling DOES apply — so the
  // contracted rates, the cap card and the Claim Allowables comparison are exactly the detail the
  // user needs. These claims render the charge-based lines INLINE, as ordinary rows in the
  // Calculation list, so one list holds every line and the Subtotal below it is the claim's real
  // expected amount. (They used to render the block stacked ABOVE the standard content with every
  // standard total suppressed, which left the claim's pricing split across two places that never
  // added up to one number — the thing this change fixes.)
  //
  // The 837 panel stays block-only whatever the scope: the charge-based calculation is derived
  // from the 835's line items, so pairing it with the 837 case-signature breakdown would put two
  // different claims' pricing under one total. That panel behaved this way before CLE-2600 too.
  const canRenderStandardContent = !!accountId && !!payerId && !!caseSignature;
  const chargeBasedLeavesUpstreamPricing =
    chargeBasedCalculation?.scope === "LINE_CHARGES" &&
    chargeBasedCalculation?.fullyOwned !== true;
  // Needs `perLines`: it is the only source for the inline rows, so a legacy claim without it
  // falls through to the block rather than showing a Calculation list missing its own lines.
  const renderChargeBasedInline =
    isChargeBasedPricing &&
    !preferEdi837 &&
    chargeBasedLeavesUpstreamPricing &&
    canRenderStandardContent &&
    (chargeBasedCalculation?.perLines?.length ?? 0) > 0;
  // Block + Claim Allowables, no fee-schedule detail. Requires `perLines`: it is the only source
  // that can price the table's rows for these claims, so a legacy row without it stays block-only
  // rather than rendering a table built from a precalc the claim was not paid on.
  const renderChargeBasedTableOnly =
    isChargeBasedPricing &&
    !preferEdi837 &&
    canRenderStandardContent &&
    chargeBasedCalculation?.scope === "LINE_CHARGES" &&
    chargeBasedCalculation?.fullyOwned === true &&
    (chargeBasedCalculation?.perLines?.length ?? 0) > 0;
  // A partial claim priced before CLE-2546 carries no `perLines`, so its charge-based lines cannot
  // be built as inline rows. It keeps the pre-CLE-2600 layout exactly — block stacked above the
  // standard content, standard totals suppressed — rather than losing the fee-schedule breakdown
  // it renders today.
  const renderChargeBasedStackedLegacy =
    isChargeBasedPricing &&
    !preferEdi837 &&
    chargeBasedLeavesUpstreamPricing &&
    canRenderStandardContent &&
    !renderChargeBasedInline;
  const renderChargeBasedBlockOnly =
    isChargeBasedPricing &&
    !renderChargeBasedInline &&
    !renderChargeBasedStackedLegacy &&
    !renderChargeBasedTableOnly;
  // Both remaining block views state the claim's one authoritative total, so the standard headers
  // below them stay suppressed — they sum raw precalc and describe the fee-schedule portion only
  // (prod 434162XX001BB: $939.83 against the block's $854.64). The inline view needs no
  // suppression: its totals are correct.
  const hideStandardTotals =
    renderChargeBasedTableOnly || renderChargeBasedStackedLegacy;

  // Fetch data using RTK Query hook
  const { data, isLoading, error, refetch } =
    useGetPrecalculatedAllowablesQuery(
      {
        accountId,
        payerId,
        caseSignature,
        dateOfService,
      },
      {
        skip:
          skip ||
          preferRevCode ||
          renderChargeBasedBlockOnly ||
          !accountId ||
          !payerId ||
          !caseSignature,
      }
    );

  // Handler for retry button
  const handleRetry = () => {
    refetch();
  };

  // Prefer claim-scoped precalc (e.g. NCCI bypass breakdown from GET/PATCH) over the generic pricing fetch.
  // CLE-2178: this calculatedData is computed server-side WITH the claim's CPT modifiers fed into the
  // pricing engine, so finalAmountInCents and the per-procedure `adjustments` already reflect them
  // (a "Modifier" adjustment line appears alongside NCCI/MPDR/etc.). No client-side re-derivation.
  // CLE-2489: the 837 section must use the claim's 837 precalc, not the 835's.
  // CLE-3071: the rev-code section must use the claim's revenue-code precalc.
  let claimScopedPrecalc: IClaim["edi837PrecalculatedAllowable"] =
    claim?.precalculatedAllowable;
  if (preferEdi837) claimScopedPrecalc = claim?.edi837PrecalculatedAllowable;
  if (preferRevCode) claimScopedPrecalc = claim?.revCodePrecalculatedAllowable;
  const effectiveCalculatedData =
    claimScopedPrecalc?.calculatedData ?? data?.data?.calculatedData;

  const effectiveMissingCodes =
    claimScopedPrecalc?.missingCodes ?? data?.data?.missingCodes;

  // CLE-2173: per-CPT total RVU (facility, DOS-year) from the precalc endpoint, used to
  // show the RVU next to each code in the Claim Allowables table so the user can see why
  // the procedures ranked the way they did.
  const rvuByCode = useMemo<Record<string, number>>(
    () =>
      (data?.data as { rvuByCode?: Record<string, number> })?.rvuByCode ?? {},
    [data]
  );

  // Transform data: extract specialties, group by specialty, calculate pricing, sort
  const { sortedSpecialties, specialtyGroupedData, totalAllowable } =
    useMemo(() => {
      if (!effectiveCalculatedData) {
        return {
          sortedSpecialties: [],
          specialtyGroupedData: new Map(),
          totalAllowable: 0,
        };
      }

      const calculatedData = effectiveCalculatedData as ICalculatedData;
      const specialties = extractUniqueSpecialties(calculatedData);
      const grouped = groupCptCodesBySpecialty(calculatedData);
      const pricing = calculateTotalPricingBySpecialty(calculatedData);
      const sorted = sortSpecialtiesByPricing(specialties, pricing);

      // CLE-2812 — a claim with the manual NCCI toggle on totals exactly like any other claim. Its
      // calculatedData is already bypass-aware server-side. This used to start from expectedAmount,
      // which is post-sequestration, so the header below took sequestration out twice (prod
      // A2458V1P5: $829.89 against an $848.81 expected) and would have added outliers twice too.

      // Calculate total allowable across ALL specialties
      // For each CPT code, group by specialty and use only the HIGHEST-priced provider per specialty
      const total = calculatedData.cptCodes.reduce((sum, cptCode) => {
        const procedures = cptCode.providerProcedures || [];

        // Group procedures by specialty
        const bySpecialty = new Map<string, typeof procedures>();
        procedures.forEach((pp) => {
          const { specialty } = pp.provider;
          let group = bySpecialty.get(specialty);
          if (!group) {
            group = [];
            bySpecialty.set(specialty, group);
          }
          group.push(pp);
        });

        // For each specialty, find the highest-priced provider
        let cptTotal = 0;
        bySpecialty.forEach((specialtyProviders) => {
          const highestPriced = specialtyProviders.reduce((max, pp) => {
            if (pp.finalAmountInCents > max.finalAmountInCents) {
              return pp;
            }
            if (
              pp.finalAmountInCents === max.finalAmountInCents &&
              pp.provider.name.localeCompare(max.provider.name) < 0
            ) {
              return pp;
            }
            return max;
          });
          cptTotal += highestPriced.finalAmountInCents;
        });

        return sum + cptTotal;
      }, 0);

      // When LOS/transfer pricing applies, the expected amount is overridden
      const totalBeforeLos =
        total + (passThroughCalculation?.totalPassThroughAmountInCents ?? 0);
      const finalTotal = losOutlierCalculation?.ruleApplied
        ? (losOutlierCalculation.expectedAllowedInCents ?? 0) +
          (passThroughCalculation?.totalPassThroughAmountInCents ?? 0)
        : totalBeforeLos;

      return {
        sortedSpecialties: sorted,
        specialtyGroupedData: grouped,
        totalAllowable: finalTotal,
      };
    }, [
      effectiveCalculatedData,
      passThroughCalculation,
      losOutlierCalculation,
      claim,
    ]);

  // Format currency helper
  const formatCurrency = (cents: number): string => {
    return (cents / 100).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  };

  // Negative values render in parens, e.g. ($127.98) — matches the ticket mock
  const formatVariance = (cents: number | null): string => {
    if (cents == null) return "-";
    if (cents < 0) return `(${formatCurrency(Math.abs(cents))})`;
    return formatCurrency(cents);
  };

  // For each CPT code, pick the highest-priced provider procedure across all
  // specialties — mirrors the total-allowable picking logic above and gives
  // us one rate/method/expected per CPT code for the claim allowables table.
  const cptCodeToTopProcedure = useMemo(() => {
    const map = new Map<string, IProviderProcedureData>();
    if (!effectiveCalculatedData) return map;
    const calculatedData = effectiveCalculatedData as ICalculatedData;
    calculatedData.cptCodes.forEach((cpt) => {
      const procs = cpt.providerProcedures ?? [];
      if (procs.length === 0) return;
      const top = procs.reduce((max, pp) =>
        pp.finalAmountInCents > max.finalAmountInCents ? pp : max
      );
      map.set(cpt.code, top);
    });
    return map;
  }, [effectiveCalculatedData]);

  // Pre-sequestration expected (cents) for each line item, indexed identically
  // to `lineItems`. Pro-rates each CPT's expected allowable across its remit
  // lines by billed units so multiple lines for the same CPT (e.g. 13160 with a
  // units=1 line and a units=266 line, or a bilateral pair) split the cpt total
  // instead of each displaying the FULL amount. The last line of each CPT
  // absorbs rounding so the per-row Expecteds sum exactly to the cpt total.
  const lineExpectedInCents = useMemo(
    () =>
      prorateExpectedByCpt(lineItems ?? [], (code) => {
        const top = cptCodeToTopProcedure.get(code);
        return top ? top.finalAmountInCents : null;
      }),
    [lineItems, cptCodeToTopProcedure]
  );

  // Pro-rate the per-claim sequestration discount (CLE-2295) across line expecteds in
  // proportion to each line's pre-sequestration expected. Without this the per-row Expected
  // would still read at the pre-sequestration contracted rate, leaving the per-row Variance
  // disagreeing with the claim's overall Difference. Computed once over the full row set
  // (not in-map) because each line's share depends on the un-discounted total.
  //
  // CLE-2534 — this split is now an APPROXIMATION, deliberately. It used to be exact: the
  // discount was expected × a flat percent, so each line's share really was that percent of its
  // expected. The discount is now the remit's actual summed CO-253, which tracks each line's
  // payer-PAID portion rather than its expected, so a line's true 253 can differ from its
  // expected-weighted share. Only the per-row attribution is affected — the loop still makes
  // sum(per-row discount) === the claim total, so sum(per-row Expected) === claim.expectedAmount
  // and every claim-level number stays exact. If per-row precision ever matters, the exact figure
  // is available per line in lineItems[].adjustments (code '253').
  const lineSequestrationDiscounts = useMemo(() => {
    const discount = claim?.sequestrationDiscountInCents ?? 0;
    const result = new Map<number, number>();
    if (!lineItems || discount <= 0) return result;
    const undiscounted: Array<{ idx: number; expectedInCents: number }> = [];
    lineItems.forEach((li, idx) => {
      const expectedInCents = lineExpectedInCents[idx];
      if (expectedInCents != null && expectedInCents > 0) {
        undiscounted.push({ idx, expectedInCents });
      }
    });
    const totalUndiscounted = undiscounted.reduce(
      (s, r) => s + r.expectedInCents,
      0
    );
    if (totalUndiscounted <= 0) return result;
    let remaining = discount;
    undiscounted.forEach((row, i) => {
      const isLast = i === undiscounted.length - 1;
      // Last row absorbs any rounding remainder so sum(per-row discount) === total discount,
      // keeping sum(per-row Expected) === claim.expectedAmount exactly.
      const lineDiscount = isLast
        ? remaining
        : Math.round((discount * row.expectedInCents) / totalUndiscounted);
      remaining -= lineDiscount;
      result.set(row.idx, lineDiscount);
    });
    return result;
  }, [claim?.sequestrationDiscountInCents, lineItems, lineExpectedInCents]);

  const allowableRows = useMemo(() => {
    if (!lineItems) return [];

    // CLE-2600 — on a charge-based claim the raw precalc is NOT what the claim was priced at:
    // a line can be capped at billed charges, zeroed by a package/case rate, or priced off
    // charges with no precalc entry at all. `perLines` is the backend's own post-cap,
    // post-bundling, post-charge-based per-line result, so it is the source of truth for the
    // Expected and Method columns here — without it this table contradicts the block above it
    // (prod 434162XX001BB: 12001 would read $502.99 instead of $407.00, 0637 "-" instead of
    // $10.80). Absent `perLines`, every row below is byte-identical to the pre-CLE-2600 output.
    const chargeBasedPerLines = chargeBasedCalculation?.perLines;
    const perLinesByCpt = new Map<string, IChargeBasedPricingLineDetail[]>();
    (chargeBasedPerLines ?? []).forEach((perLine) => {
      if (!perLine.cptCode) return;
      const existing = perLinesByCpt.get(perLine.cptCode);
      if (existing) existing.push(perLine);
      else perLinesByCpt.set(perLine.cptCode, [perLine]);
    });
    // A CPT can repeat across lines; both lists are line-ordered, so consume each CPT's
    // perLines in order and fall back to the pro-rated precalc for any line left over.
    const perLineCursorByCpt = new Map<string, number>();
    // CPTs whose fee-schedule price the lesser-of cap bound — the "which line it hit" the
    // Method column has to name, since the Contracted Rate column still shows the pre-cap rate.
    const chargeCappedCptCodes = new Set(
      lesserOfChargesCalculation?.fired
        ? (lesserOfChargesCalculation.perLineCaps ?? []).map(
            (cap) => cap.cptCode
          )
        : []
    );

    return lineItems.map((li, idx) => {
      const top = cptCodeToTopProcedure.get(li.cptCode);
      const contractedRateInCents = top?.priceInCents ?? null;
      const cursor = perLineCursorByCpt.get(li.cptCode) ?? 0;
      const perLine = perLinesByCpt.get(li.cptCode)?.[cursor] ?? null;
      if (perLine) perLineCursorByCpt.set(li.cptCode, cursor + 1);
      // What this line was priced at: the rule's own per-line result when we have one,
      // else the pro-rated share of this CPT's expected allowable (see lineExpectedInCents).
      let expectedInCents: number | null = perLine
        ? perLine.pricedExpectedInCents
        : lineExpectedInCents[idx] ?? null;
      // CLE-2295: subtract this line's share of the claim-level sequestration discount.
      // No-op when the rule didn't apply (map is empty); otherwise the Variance column
      // below reconciles with the claim's overall Difference shown in Financial Summary.
      if (expectedInCents != null && lineSequestrationDiscounts.has(idx)) {
        expectedInCents -= lineSequestrationDiscounts.get(idx) ?? 0;
      }
      // Dedupe method descriptions so multi-unit lines (e.g. 13160 with 266
      // adjustments, or NCCI codes with the same edit applied per unit) show
      // each distinct method once. MPR-style descriptions are normalized by
      // stripping the "Nth procedure" ordinal so 2nd-Nth collapse together.
      const seenKeys = new Set<string>();
      const methodParts: string[] = [];
      (top?.adjustments ?? []).forEach((adj) => {
        const desc = adj.description;
        if (!desc) return;
        const key = descriptionGroupKey(desc);
        if (seenKeys.has(key)) return;
        seenKeys.add(key);
        const ord = parseOrdinalProcedurePrefix(desc);
        if (ord) {
          // " is discounted by 50%, ..." → "Discounted by 50%, ..."
          const rest = ord.rest.replace(/^\s+is\s+/i, " ").trimStart();
          methodParts.push(rest.charAt(0).toUpperCase() + rest.slice(1));
        } else {
          methodParts.push(desc);
        }
      });
      // Note: modifier-based adjustments (CLE-2178) are applied upstream in
      // `effectiveCalculatedData`, so `top.finalAmountInCents` is already
      // modifier-adjusted and the modifier note is already in `top.adjustments`.
      let method: string | null = null;
      if (top) {
        method =
          methodParts.length > 0 ? methodParts.join("; ") : "Paid at 100%";
      }
      // CLE-2600 — say how the line was ACTUALLY priced, matching the block above.
      if (perLine) {
        if (perLine.source === "CHARGE_BASED") {
          // The precalc never priced this line (it's usually in missingCodes), so there is no
          // adjustment list to describe — the rule's own multiplier is the whole story.
          method = `${formatMultiplierPercent(
            perLine.multiplier ?? 0
          )} of billed charges`;
          if (perLine.capApplied) {
            method += `; capped at ${formatCurrency(
              perLine.maxAmountInCents ?? 0
            )}`;
          }
        } else if (perLine.source === "BUNDLED") {
          method =
            perLine.suppressedByDescription ||
            "Bundled — not separately reimbursed";
        } else if (perLine.source === "PACKAGED") {
          // CLE-2627 — the charge-based contract packaged this code to $0.
          method = "Packaged — not separately payable under this contract";
        } else if (chargeCappedCptCodes.has(li.cptCode)) {
          // FEE_SCHEDULE line the lesser-of cap bound: keep the adjustment-derived method and
          // name the cap, which is why Expected sits below the Contracted Rate beside it.
          method = method
            ? `${method}; capped at billed charges`
            : "Capped at billed charges";
        }
      }
      // CLE-2173: "Allowed" = effective allowed (paid + standard PR: deductible/
      // coinsurance/copay), matching the claim-level claimAmount. Using the raw
      // lineAllowed here counted non-standard PR (e.g. a bundled line mis-coded PR-97),
      // inflating allowed + variance vs. what claim review shows.
      const allowedInCents = Math.round(effectiveLineAllowed(li) * 100);
      const varianceInCents =
        expectedInCents != null ? allowedInCents - expectedInCents : null;
      return {
        key: `${li.cptCode}-${li.modifier ?? ""}-${
          li.lineControlNumber ?? idx
        }`,
        cptCode: li.cptCode,
        modifier: li.modifier,
        units: li.billedUnits,
        allowedInCents,
        contractedRateInCents,
        method,
        expectedInCents,
        varianceInCents,
      };
    });
  }, [
    lineItems,
    cptCodeToTopProcedure,
    lineExpectedInCents,
    lineSequestrationDiscounts,
    chargeBasedCalculation,
    lesserOfChargesCalculation,
  ]);

  // Charge-Based Pricing short-circuit: the rule priced this claim off the claim total (or off
  // per-line data we no longer have), so the standard precalc / lesser-of / outlier branches have
  // nothing to add and there is nothing to build a per-line table from. Render the rule block
  // synchronously — the precalc fetch is also skipped above so there is nothing in flight. The
  // lesser-of cap rides along because the block is the only place it can be shown for these claims.
  if (renderChargeBasedBlockOnly && chargeBasedCalculation) {
    return (
      <ChargeBasedPricingBlock
        calculation={chargeBasedCalculation}
        lesserOfChargesCalculation={lesserOfChargesCalculation}
      />
    );
  }

  // CLE-2600 — a charge-based claim carries its own precalc on the claim object, and the
  // account/payer/caseSignature lookup routinely 404s for it. Replacing the whole tab with a
  // retry box would hide the rule block and every embedded detail we already have, so fall
  // back to the embedded precalc and keep rendering.
  const showError = !!error && !isChargeBasedPricing;

  return (
    <>
      {/* Legacy-stacked / table-only views: the rule block first — its Total Allowable is the claim's
          expected amount and the page's single authoritative total.
          No lesserOfChargesCalculation in either, for two different reasons: legacy-stacked leaves
          the cap to the standard LesserOfChargesCard below, and on a FULLY-OWNED claim the backend
          discarded the cap outright (the claim total short-circuits to the sum of priced lines),
          so a "−$X" cap row here would state a reduction that is not in the total. */}
      {(renderChargeBasedTableOnly || renderChargeBasedStackedLegacy) &&
        chargeBasedCalculation && (
          <ChargeBasedPricingBlock
            calculation={chargeBasedCalculation}
            // Narrow to the rule's own lines only when a fee-schedule breakdown follows. On a
            // fully-owned claim every line is charge-based, so the full list IS the story.
            affectedLinesOnly={renderChargeBasedStackedLegacy}
          />
        )}

      {isLoading && (
        <LoadingContainer>
          <ProgressSpinner />
        </LoadingContainer>
      )}

      {/* Error state: API request failed */}
      {!isLoading && showError && (
        <ErrorMessage error={error} onRetry={handleRetry} />
      )}

      {/* No data state: API returned successfully but no data or empty specialties.
          If pass-through data exists, we fall through to the success state below.
          Never in the table-only view: the precalc lookup routinely finds nothing for a
          fully-owned claim, and "no data" would be false next to a complete rule audit. */}
      {!isLoading &&
        !showError &&
        !renderChargeBasedTableOnly &&
        (!effectiveCalculatedData || sortedSpecialties.length === 0) &&
        !passThroughCalculation && <NoDataMessage />}

      {/* Pass-through only state: no fee schedule data, but pass-through exists */}
      {!isLoading &&
        !showError &&
        !renderChargeBasedTableOnly &&
        sortedSpecialties.length === 0 &&
        passThroughCalculation && (
          <>
            {/* Suppressed under the block: this total sums raw precalc + pass-through,
                which is the fee-schedule portion only, and the block above already states
                the claim's one authoritative total. */}
            {!hideStandardTotals && (
              <TotalAllowableSection>
                <TotalAllowableLabel>Total Allowable:</TotalAllowableLabel>
                <TotalAllowableAmount>
                  {formatCurrency(
                    (passThroughCalculation.totalPassThroughAmountInCents ??
                      0) + (outlierPaymentInCents ?? 0)
                  )}
                </TotalAllowableAmount>
              </TotalAllowableSection>
            )}
            <SpecialtyTabContent
              chargeBasedPricingCalculation={
                renderChargeBasedInline ? chargeBasedCalculation : undefined
              }
              cptCodes={[]}
              specialty=""
              rvuByCode={rvuByCode}
              claim={claim}
              lineItems={lineItems}
              outlierPaymentInCents={outlierPaymentInCents}
              outlierCalculation={outlierCalculation}
              passThroughCalculation={passThroughCalculation}
              chargeAmountInCents={chargeAmountInCents}
              losOutlierCalculation={losOutlierCalculation}
              stopLossPaymentInCents={stopLossPaymentInCents}
              stopLossCalculation={stopLossCalculation}
              chargeOutlierPaymentInCents={chargeOutlierPaymentInCents}
              chargeOutlierCalculation={chargeOutlierCalculation}
              inpatientOutlierCalculation={inpatientOutlierCalculation}
              lesserOfChargesCalculation={lesserOfChargesCalculation}
              sequestrationDiscountInCents={claim?.sequestrationDiscountInCents}
            />
          </>
        )}

      {/* Success state: Display total and tabs with data. The table-only view enters here too,
          however little the precalc returned — its Claim Allowables rows come from `perLines`. */}
      {!isLoading &&
        !showError &&
        (renderChargeBasedTableOnly ||
          (effectiveCalculatedData && sortedSpecialties.length > 0)) && (
          <>
            {/* Total Allowable Section — suppressed under the block, where it would sum the
                raw precalc (prod 434162XX001BB: $939.83) against the block's $854.64. */}
            {!hideStandardTotals && (
              <TotalAllowableSection>
                <TotalAllowableLabel>Total Allowable:</TotalAllowableLabel>
                <TotalAllowableAmount>
                  {formatCurrency(
                    // CLE-2600 — on an inline charge-based claim the raw precalc is the
                    // fee-schedule portion only. The rule's own total IS the claim's
                    // expectedAmount (edi_conversion.service.ts:3651-3653), and it already
                    // reflects the lesser-of cap, which ran before it.
                    (renderChargeBasedInline && chargeBasedCalculation
                      ? chargeBasedCalculation.pricedExpectedInCents
                      : totalAllowable + (outlierPaymentInCents ?? 0)) -
                      // CLE-2295: subtract the per-claim Medicare/MA sequestration discount when
                      // applied server-side, so the displayed header total matches the claim's
                      // post-sequestration expectedAmount.
                      (claim?.sequestrationDiscountInCents ?? 0)
                  )}
                </TotalAllowableAmount>
              </TotalAllowableSection>
            )}

            {/* Missing Codes Warning (if any) — exclude codes handled by pass-through */}
            {(() => {
              const passThroughCodes = new Set(
                passThroughCalculation?.codes.map((c) => c.code) ?? []
              );
              // CLE-2600 — in the inline view a charge-based code is "missing" from the FEE
              // SCHEDULE, which is exactly why the rule priced it; it is not missing from the
              // total, and it now has its own row in the Calculation list. The banner asserted
              // the opposite of the number two inches above it (prod 434162XX001BB said 0637 was
              // "NOT included in the total allowable above" while its $10.20 was inside the
              // $864.66). Scoped to the inline view: under a block the banner still usefully says
              // the precalc never priced the code.
              const chargeBasedCodes = new Set(
                renderChargeBasedInline
                  ? (chargeBasedCalculation?.perLines ?? [])
                      .filter((perLine) => perLine.source === "CHARGE_BASED")
                      .map((perLine) => perLine.cptCode)
                      .filter((code): code is string => !!code)
                  : []
              );
              const trulyMissing = (effectiveMissingCodes ?? []).filter(
                (mc) =>
                  !passThroughCodes.has(mc.code) &&
                  !chargeBasedCodes.has(mc.code)
              );
              return trulyMissing.length > 0 ? (
                <MissingCodesSection>
                  <MissingCodesTitle>
                    Incomplete Calculation - Missing Procedure Codes
                  </MissingCodesTitle>
                  <MissingCodesText>
                    The following procedure codes from the case signature were
                    not found in our database and are NOT included in the total
                    allowable above:{" "}
                    <strong>
                      {trulyMissing
                        .map((mc) => `${mc.code} (qty: ${mc.quantity})`)
                        .join(", ")}
                    </strong>
                  </MissingCodesText>
                </MissingCodesSection>
              ) : null;
            })()}

            {/* Specialty Tabs — the fee schedule's own story: contracted rates, the
                calculation list and the outlier / cap cards. Omitted in the table-only
                view, where the fee schedule contributed nothing to the expected amount and
                showing it would describe pricing this claim never got. */}
            {!renderChargeBasedTableOnly && (
              <TabView>
                {sortedSpecialties.map((specialty, idx) => {
                  const cptCodes = specialtyGroupedData.get(specialty) || [];
                  return (
                    <TabPanel
                      key={specialty}
                      header={formatSpecialtyName(specialty)}
                    >
                      <SpecialtyTabContent
                        // First tab only — the rule is whole-claim and its lines have no
                        // specialty, same convention as pass-through and the outliers below.
                        chargeBasedPricingCalculation={
                          idx === 0 && renderChargeBasedInline
                            ? chargeBasedCalculation
                            : undefined
                        }
                        cptCodes={cptCodes}
                        specialty={specialty}
                        rvuByCode={rvuByCode}
                        claim={claim}
                        lineItems={lineItems}
                        outlierPaymentInCents={outlierPaymentInCents}
                        outlierCalculation={outlierCalculation}
                        // Pass-through and LOS adjustment only on first tab to avoid double-counting
                        passThroughCalculation={
                          idx === 0 ? passThroughCalculation : undefined
                        }
                        chargeAmountInCents={
                          idx === 0 ? chargeAmountInCents : undefined
                        }
                        losOutlierCalculation={
                          idx === 0 ? losOutlierCalculation : undefined
                        }
                        stopLossPaymentInCents={
                          idx === 0 ? stopLossPaymentInCents : undefined
                        }
                        stopLossCalculation={
                          idx === 0 ? stopLossCalculation : undefined
                        }
                        chargeOutlierPaymentInCents={
                          idx === 0 ? chargeOutlierPaymentInCents : undefined
                        }
                        chargeOutlierCalculation={
                          idx === 0 ? chargeOutlierCalculation : undefined
                        }
                        inpatientOutlierCalculation={
                          idx === 0 ? inpatientOutlierCalculation : undefined
                        }
                        lesserOfChargesCalculation={
                          idx === 0 ? lesserOfChargesCalculation : undefined
                        }
                        sequestrationDiscountInCents={
                          idx === 0
                            ? claim?.sequestrationDiscountInCents
                            : undefined
                        }
                      />
                    </TabPanel>
                  );
                })}
              </TabView>
            )}

            {/* Claim Allowables Table */}
            {allowableRows.length > 0 && (
              <ClaimAllowablesSection>
                <ClaimAllowablesTitle>Claim Allowables</ClaimAllowablesTitle>
                <DataTable
                  value={allowableRows}
                  dataKey="key"
                  stripedRows
                  responsiveLayout="scroll"
                  style={{ fontSize: "0.9rem" }}
                >
                  <Column
                    field="cptCode"
                    header="Code"
                    style={{ minWidth: "5rem" }}
                  />
                  <Column
                    field="modifier"
                    header="Modifier"
                    body={(row) => row.modifier || "-"}
                    style={{ minWidth: "5rem" }}
                  />
                  <Column
                    field="units"
                    header="Unit"
                    body={(row) => row.units ?? "-"}
                    style={{ minWidth: "4rem" }}
                  />
                  <Column
                    field="allowedInCents"
                    header="Allowed Amount"
                    body={(row) => formatCurrency(row.allowedInCents)}
                    style={{ minWidth: "8rem" }}
                  />
                  <Column
                    field="contractedRateInCents"
                    header="Contracted Rate"
                    body={(row) =>
                      row.contractedRateInCents != null
                        ? formatCurrency(row.contractedRateInCents)
                        : "-"
                    }
                    style={{ minWidth: "8rem" }}
                  />
                  <Column
                    field="method"
                    header="Method"
                    body={(row) => row.method ?? "-"}
                    style={{
                      minWidth: "16rem",
                      whiteSpace: "normal",
                      lineHeight: 1.4,
                    }}
                  />
                  <Column
                    field="expectedInCents"
                    header="Expected"
                    body={(row) =>
                      row.expectedInCents != null
                        ? formatCurrency(row.expectedInCents)
                        : "-"
                    }
                    style={{ minWidth: "7rem" }}
                  />
                  <Column
                    field="varianceInCents"
                    header="Variance"
                    body={(row) => formatVariance(row.varianceInCents)}
                    style={{ minWidth: "7rem" }}
                  />
                </DataTable>
              </ClaimAllowablesSection>
            )}
          </>
        )}
    </>
  );
}
